import React from 'react';
import { Treemap, ResponsiveContainer, Tooltip } from 'recharts';
import { useNavigate } from 'react-router-dom';

interface Project {
  name: string;
  health_status: string;
  client_name?: string;
  project_id?: string;
  client_id?: string;
  infrastructure_budget?: number;
  infrastructure_spent?: number;
  infrastructure_estimated?: number;
  progress?: number;
  days_elapsed?: number;
  days_total?: number;
  time_elapsed_pct?: number;
}

interface ProjectHealthData {
  projects?: Project[];
}

interface Props {
  data?: ProjectHealthData;
  loading?: boolean;
  error?: string;
}

interface TreemapItem {
  name: string;
  size: number;
  health: string;
  client?: string;
  project_id?: string;
  client_id?: string;
  spent: number;
  estimated: number;
  progress: number;
  days_elapsed: number;
  days_total: number;
  time_elapsed_pct: number;
}

interface CustomizedContentProps {
  x?: number;
  y?: number;
  width?: number;
  height?: number;
  name?: string;
  health?: string;
  [key: string]: unknown;
}

interface TooltipPayloadItem {
  payload?: TreemapItem;
}

interface CustomTooltipProps {
  active?: boolean;
  payload?: TooltipPayloadItem[];
}

const ProjectHealthHeatmap: React.FC<Props> = ({ data, loading, error }) => {
  const navigate = useNavigate();

  if (loading) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-sky-400"></div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <p className="text-red-400 mb-2">Failed to load project health data</p>
          <p className="text-gray-400 text-sm">{error}</p>
        </div>
      </div>
    );
  }

  if (!data?.projects || data.projects.length === 0) {
    return (
      <div className="flex items-center justify-center h-full">
        <div className="text-center">
          <p className="text-gray-400 mb-2">No projects to display</p>
          <p className="text-gray-500 text-sm">
            Projects will appear here once they have timeline data
          </p>
        </div>
      </div>
    );
  }

  const { projects } = data;

  const treemapData: TreemapItem[] = projects.map((project) => ({
    name: project.name,
    size: project.infrastructure_budget || 1,
    health: project.health_status,
    client: project.client_name,
    project_id: project.project_id,
    client_id: project.client_id,
    spent: project.infrastructure_spent || 0,
    estimated: project.infrastructure_estimated || 0,
    progress: project.progress || 0,
    days_elapsed: project.days_elapsed || 0,
    days_total: project.days_total || 0,
    time_elapsed_pct: project.time_elapsed_pct || 0
  }));

  const getColor = (status: string): string => {
    switch (status) {
      case 'green':
        return '#10b981';
      case 'yellow':
        return '#f59e0b';
      case 'red':
        return '#ef4444';
      default:
        return '#6b7280';
    }
  };

  const CustomizedContent: React.FC<CustomizedContentProps> = (props) => {
    const { x = 0, y = 0, width = 0, height = 0, name, health } = props;

    if (width < 40 || height < 30 || !name) {
      return null;
    }

    const fontSize = Math.max(12, Math.min(width / 8, height / 4, 16));
    const maxChars = Math.floor((width * 0.85) / (fontSize * 0.6));
    const displayText = name.length > maxChars ? name.substring(0, maxChars - 3) + '...' : name;

    return (
      <g>
        <rect
          x={x}
          y={y}
          width={width}
          height={height}
          style={{
            fill: getColor(health ?? ''),
            stroke: '#1e293b',
            strokeWidth: 2,
            cursor: 'pointer',
            opacity: 0.9
          }}
          className="transition-opacity hover:opacity-100"
        />
        <text
          x={x + width / 2}
          y={y + height / 2}
          textAnchor="middle"
          fill="white"
          fontSize={fontSize}
          fontWeight="700"
          style={{
            textShadow: '0 0 3px rgba(0,0,0,0.8), 0 0 5px rgba(0,0,0,0.6)',
            paintOrder: 'stroke fill',
            stroke: 'rgba(0,0,0,0.3)',
            strokeWidth: 0.5
          }}
        >
          {displayText}
        </text>
      </g>
    );
  };

  const CustomTooltip: React.FC<CustomTooltipProps> = ({ active, payload }) => {
    if (!active || !payload?.length) {
      return null;
    }

    const tooltipData = payload[0].payload;
    if (!tooltipData) return null;

    const getStatusBadge = (status: string) => {
      const badges: Record<string, { label: string; bg: string; text: string }> = {
        green: { label: 'On Track', bg: 'bg-emerald-500/20', text: 'text-emerald-400' },
        yellow: { label: 'At Risk', bg: 'bg-amber-500/20', text: 'text-amber-400' },
        red: { label: 'Off Track', bg: 'bg-red-500/20', text: 'text-red-400' },
        unknown: { label: 'Unknown', bg: 'bg-gray-500/20', text: 'text-gray-400' }
      };
      const badge = badges[status] || badges.unknown;
      return (
        <span className={`px-2 py-1 rounded text-xs font-medium ${badge.bg} ${badge.text}`}>
          {badge.label}
        </span>
      );
    };

    const formatCurrency = (value: number): string => {
      if (value >= 1000000) {
        return `$${(value / 1000000).toFixed(1)}M`;
      } else if (value >= 1000) {
        return `$${(value / 1000).toFixed(0)}K`;
      } else {
        return `$${value.toFixed(0)}`;
      }
    };

    const spentPct = tooltipData.size > 0 ? (tooltipData.spent / tooltipData.size * 100).toFixed(0) : 0;

    return (
      <div className="bg-gray-800 border border-gray-700 rounded-lg p-3 shadow-xl max-w-xs">
        <div className="space-y-2">
          <div>
            <p className="font-semibold text-white text-sm">{tooltipData.name}</p>
            <p className="text-gray-400 text-xs">{tooltipData.client}</p>
          </div>

          <div className="pt-2 border-t border-gray-700">
            {getStatusBadge(tooltipData.health)}
          </div>

          <div className="space-y-1 text-xs">
            <div className="flex justify-between">
              <span className="text-gray-400">Budget:</span>
              <span className="text-white font-medium">{formatCurrency(tooltipData.size)}</span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Spent:</span>
              <span className="text-white font-medium">
                {formatCurrency(tooltipData.spent)} ({spentPct}%)
              </span>
            </div>
            <div className="flex justify-between">
              <span className="text-gray-400">Progress:</span>
              <span className="text-white font-medium">{tooltipData.progress}%</span>
            </div>
            {tooltipData.days_total > 0 && (
              <div className="flex justify-between">
                <span className="text-gray-400">Timeline:</span>
                <span className="text-white font-medium">
                  {tooltipData.days_elapsed}/{tooltipData.days_total} days ({tooltipData.time_elapsed_pct}%)
                </span>
              </div>
            )}
          </div>

          <div className="pt-2 text-xs text-gray-400 italic">
            Click to view project details
          </div>
        </div>
      </div>
    );
  };

  const handleClick = (clickData: unknown): void => {
    const item = clickData as TreemapItem | null;
    if (item?.project_id && item.client_id) {
      navigate(`/hub/projects?client=${item.client_id}&project=${item.project_id}`);
    }
  };

  return (
    <div className="h-full flex flex-col">
      <div className="flex-1 min-h-0 p-2">
        <ResponsiveContainer width="100%" height="100%">
          <Treemap
            data={treemapData}
            dataKey="size"
            stroke="#1e293b"
            fill="#8884d8"
            content={<CustomizedContent />}
            onClick={handleClick}
          >
            <Tooltip content={<CustomTooltip />} />
          </Treemap>
        </ResponsiveContainer>
      </div>

      <div className="flex items-center justify-center gap-4 px-3 py-2 border-t border-gray-700 text-xs">
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded" style={{ backgroundColor: '#10b981' }}></div>
          <span className="text-gray-400">On Track</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded" style={{ backgroundColor: '#f59e0b' }}></div>
          <span className="text-gray-400">At Risk</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded" style={{ backgroundColor: '#ef4444' }}></div>
          <span className="text-gray-400">Off Track</span>
        </div>
        <div className="flex items-center gap-1.5">
          <div className="w-3 h-3 rounded" style={{ backgroundColor: '#6b7280' }}></div>
          <span className="text-gray-400">Unknown</span>
        </div>
      </div>
    </div>
  );
};

export default ProjectHealthHeatmap;
